import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import RecoveryPanel from "../components/RecoveryPanel";
import Badge from "../components/ui/Badge";
import Button from "../components/ui/Button";
import Card from "../components/ui/Card";
import LoadingState from "../components/ui/LoadingState";
import { fetchMyProfile } from "../api/client";

function joinValues(values) {
  return Array.isArray(values) && values.length ? values.join(", ") : "Not set";
}

export default function Profile() {
  const [profileState, setProfileState] = useState({
    loading: true,
    error: "",
    data: null,
  });

  const loadProfileDetails = () => {
    setProfileState({ loading: true, error: "", data: null });

    fetchMyProfile()
      .then((response) => {
        setProfileState({ loading: false, error: "", data: response.data });
      })
      .catch(() => {
        setProfileState({
          loading: false,
          error: "We could not load your profile. Please sign in again.",
          data: null,
        });
      });
  };

  useEffect(() => {
    loadProfileDetails();
  }, []);

  if (profileState.loading) {
    return (
      <div className="px-4 py-10">
        <LoadingState title="Loading your profile" message="Gathering your saved vibe details." />
      </div>
    );
  }

  if (profileState.error) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-10">
        <h2 className="mb-4 font-display text-3xl font-bold text-hookd-cream">Your Hook'd Profile</h2>
        <RecoveryPanel
          title="Profile did not load"
          message={profileState.error}
          actions={[
            {
              label: "Retry",
              onClick: loadProfileDetails,
            },
            {
              label: "Sign In",
              to: "/login",
            },
          ]}
        />
      </div>
    );
  }

  const data = profileState.data;
  const profile = data?.profile;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      <Card className="mb-6 p-6 sm:p-8" glow="purple">
        <div className="grid gap-6 lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="min-w-0">
            <Badge variant="gold" className="mb-4">Vibe Seeker</Badge>
            <h1 className="font-display text-4xl font-bold text-hookd-cream">
              Welcome back, The Empress
            </h1>
            <p className="mt-2 text-sm text-hookd-muted break-words">{data?.email}</p>
          </div>
          <div className="grid gap-3 sm:flex sm:flex-wrap">
            <Button as={Link} to="/saved-shows" variant="outline">Saved shows</Button>
            <Button as={Link} to="/quiz">Update quiz preferences</Button>
          </div>
        </div>
      </Card>

      {!profile ? (
        <Card className="p-6">
          <p className="text-hookd-muted">No saved profile yet.</p>
          <Link to="/quiz" className="mt-3 inline-flex min-h-11 items-center text-hookd-empress font-semibold underline">
            Take the quiz to save your preferences.
          </Link>
        </Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
          <Card className="p-6">
            <h2 className="mb-5 font-display text-3xl font-bold text-hookd-cream">Preferences</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <p><span className="font-semibold text-hookd-gold">Age:</span> {profile.age}</p>
              <p><span className="font-semibold text-hookd-gold">Genres:</span> {joinValues(profile.favorite_genres)}</p>
              <p><span className="font-semibold text-hookd-gold">Moods:</span> {joinValues(profile.favorite_moods)}</p>
              <p><span className="font-semibold text-hookd-gold">Platforms:</span> {joinValues(profile.available_platforms)}</p>
              <p><span className="font-semibold text-hookd-gold">Laugh:</span> {profile.laugh_pref || "Not set"}</p>
              <p>
                <span className="font-semibold text-hookd-gold">Commitment:</span>{" "}
                {profile.commitment_flexible ? "Open to anything" : profile.commitment_pref || "Not set"}
              </p>
              <p><span className="font-semibold text-hookd-gold">Vibe:</span> {profile.vibe_pref || "Not set"}</p>
              <p><span className="font-semibold text-hookd-gold">Representation:</span> {joinValues(profile.cultural_tags)}</p>
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="mb-5 font-display text-3xl font-bold text-hookd-cream">Beta History</h2>
            <div className="grid gap-4">
              <div className="rounded-hookd border border-hookd-line bg-white/5 p-4">
                <p className="text-3xl font-bold text-hookd-empress">{data?.quiz_history?.length || 0}</p>
                <p className="text-sm text-hookd-muted">Quiz snapshots</p>
              </div>
              <div className="rounded-hookd border border-hookd-line bg-white/5 p-4">
                <p className="text-3xl font-bold text-hookd-empress">{data?.feedback_refs?.length || 0}</p>
                <p className="text-sm text-hookd-muted">Feedback records</p>
              </div>
              <div className="rounded-hookd border border-hookd-line bg-white/5 p-4">
                <p className="text-3xl font-bold text-hookd-empress">{data?.saved_show_ids?.length || 0}</p>
                <p className="text-sm text-hookd-muted">Saved shows</p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
